import puduLogo from "@/assets/pudu-logo.png";
import { Facebook, Linkedin, Instagram, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { Button } from "./ui/button";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="mt-16 pt-12 border-t border-[hsl(var(--line))] bg-gradient-to-b from-transparent to-muted/10">
      {/* Mini CTA Section */}
      <div className="mb-12 text-center">
        <div className="max-w-3xl mx-auto glass-effect border border-[hsl(var(--line))] rounded-2xl p-8">
          <h3 className="font-orbitron text-2xl font-bold mb-3 text-foreground">
            🚀 Let's Automate Your Business
          </h3>
          <p className="text-muted-foreground mb-6">
            Ready to transform your operations with AI automation?
          </p>
          <Button
            asChild
            size="lg"
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold rounded-xl px-8 py-6 shadow-[0_0_20px_rgba(0,255,200,0.4)]"
          >
            <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
              📅 Book Your Free Demo
            </a>
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-8 mb-12">
        {/* Company Info */}
        <div>
          <h3 className="font-orbitron font-bold text-xl mb-4">
            <span className="text-primary">Neil AI</span> Global Solutions
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Empowering businesses to go fully autonomous through cutting-edge AI software, robotics, and automation technology.
          </p>
          <div className="space-y-2 text-sm text-muted-foreground">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>611 Summit Ave, Suite 14<br />Greensboro, NC 27405</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 flex-shrink-0" />
              <a href="tel:+13369624539" className="hover:text-primary transition-colors">
                (336) 962-4539
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 flex-shrink-0" />
              <a href="mailto:neilai@neilaiglobalsolutionsllc.com" className="hover:text-primary transition-colors">
                Contact Us
              </a>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-bold text-foreground mb-4">Quick Links</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>
              <a href="/" className="hover:text-primary transition-colors">
                Home
              </a>
            </li>
            <li>
              <a href="/about" className="hover:text-primary transition-colors">
                About Us
              </a>
            </li>
            <li>
              <a href="/robots" className="hover:text-primary transition-colors">
                AI Tools & Robots
              </a>
            </li>
            <li>
              <a href="/case-studies" className="hover:text-primary transition-colors">
                Case Studies
              </a>
            </li>
            <li>
              <a href="/accessories" className="hover:text-primary transition-colors">
                Shop Accessories
              </a>
            </li>
            <li>
              <a href="#contact" className="hover:text-primary transition-colors">
                Contact Us
              </a>
            </li>
          </ul>
        </div>

        {/* Resources */}
        <div>
          <h4 className="font-bold text-foreground mb-4">Resources</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>
              <a href="#neil-roi" className="hover:text-primary transition-colors">
                ROI Calculator
              </a>
            </li>
            <li>
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                Book a Demo
              </a>
            </li>
            <li>
              <a href="https://www.neilaiglobalsolutionsllc.com/?fbclid=IwVERFWAN_VjVleHRuA2FlbQIxMABzcnRjBmFwcF9pZAo2NjI4NTY4Mzc5AAEe7T400tL8D_48_O_uIQ7s-e_kC1ORjakJq5xCLhFpHFhVByR2D-zDNmD7Ej8_aem_0fzOK48yXq6Z9RuC7TvEUA" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                Customer Login
              </a>
            </li>
            <li>
              <a href="/affiliate" className="hover:text-primary transition-colors">
                Affiliate Portal
              </a>
            </li>
            <li>
              <a href="/terms" className="hover:text-primary transition-colors">
                Terms & Conditions
              </a>
            </li>
            <li>
              <a href="/privacy" className="hover:text-primary transition-colors">
                Privacy Policy
              </a>
            </li>
            <li>
              <a href="/careers" className="hover:text-primary transition-colors">
                Careers
              </a>
            </li>
            <li>
              <a href="mailto:support@neilaiglobalsolutionsllc.com" className="hover:text-primary transition-colors">
                Support
              </a>
            </li>
          </ul>
        </div>

        {/* Industries */}
        <div>
          <h4 className="font-bold text-foreground mb-4">Industries We Serve</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>
              <a href="/restaurants" className="hover:text-primary transition-colors">
                🍽️ Restaurants & Food Service
              </a>
            </li>
            <li>
              <a href="/hotels" className="hover:text-primary transition-colors">
                🏨 Hotels & Hospitality
              </a>
            </li>
            <li>
              <a href="/healthcare" className="hover:text-primary transition-colors">
                🏥 Healthcare Facilities
              </a>
            </li>
            <li>
              <a href="/education" className="hover:text-primary transition-colors">
                🎓 Education & Campuses
              </a>
            </li>
            <li>
              <a href="/warehouses" className="hover:text-primary transition-colors">
                📦 Warehouses & Logistics
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Social Media & Bottom Section */}
      <div className="pt-8 border-t border-[hsl(var(--line))]">
        {/* Social Icons */}
        <div className="flex justify-center gap-4 mb-6">
          <a 
            href="https://www.facebook.com/share/1FqmyxEAdi/?mibextid=wwXIfr" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-all hover:scale-110"
            aria-label="Facebook"
          >
            <Facebook className="w-5 h-5 text-primary" />
          </a>
          <a 
            href="https://www.tiktok.com/@neil_ai_global?_r=1&_t=ZT-91nGi8vJKj9" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-all hover:scale-110"
            aria-label="TikTok"
          >
            <svg className="w-5 h-5 text-primary" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
            </svg>
          </a>
          <a 
            href="https://www.linkedin.com/in/albert-saunders-62a26137a" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-all hover:scale-110"
            aria-label="LinkedIn"
          >
            <Linkedin className="w-5 h-5 text-primary" />
          </a>
          <a 
            href="https://www.instagram.com/neil_ai500?igsh=MW5idHpoejlpMjB1ZA%3D%3D&utm_source=qr" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-all hover:scale-110"
            aria-label="Instagram"
          >
            <Instagram className="w-5 h-5 text-primary" />
          </a>
          <a 
            href="https://youtube.com/@neilai93?si=yE4WECNdysq-iZbo" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-all hover:scale-110"
            aria-label="YouTube"
          >
            <Youtube className="w-5 h-5 text-primary" />
          </a>
        </div>

        {/* Authorized Dealer */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <span className="text-sm text-muted-foreground">Authorized Dealer:</span>
          <img src={puduLogo} alt="PUDU Robotics" className="h-8 opacity-80" />
        </div>

        {/* Copyright */}
        <div className="text-center text-sm text-muted-foreground space-y-2">
          <p>© {currentYear} Neil AI Global Solutions LLC. All rights reserved.</p>
          <p>Greensboro, NC | Serving the Southeastern United States</p>
        </div>
      </div>

      {/* Trust & Legal Info */}
      <div className="border-t border-border/50 pt-6 text-center space-y-2">
        <p className="text-xs text-muted-foreground">
          Neil AI Global Solutions LLC • Greensboro, NC, USA •{" "}
          Phone: <a href="tel:+13369624539" className="text-accent hover:underline">+1 (336) 962-4539</a> •{" "}
          Email: <a href="mailto:neilai@neilaiglobalsolutionsllc.com" className="text-accent hover:underline">neilai@neilaiglobalsolutionsllc.com</a>
        </p>
        <p className="text-xs text-muted-foreground/80">
          We never sell your data. Payments are processed securely via trusted providers.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
